import { useState } from 'react';
import { Bell, CheckCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useNotifications } from './useNotifications';
import { getNotifications, markAllRead, markRead, acknowledgeNotification } from '@/api/employee-notifications';
import { formatNotificationMessage } from '@shared/types';
import type { EmployeeNotificationDTO } from '@shared/types';
import { cn } from '@/lib/utils';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  return `${days} д назад`;
}

export function NotificationBell() {
  const { unreadCount } = useNotifications();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['employee-notifications', 'list'],
    queryFn: () => getNotifications(50),
    enabled: open,
  });

  const markAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-notifications'] });
    },
  });

  const markOneMutation = useMutation({
    mutationFn: markRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-notifications'] });
    },
  });

  const ackMutation = useMutation({
    mutationFn: acknowledgeNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-notifications'] });
    },
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleNotificationClick = (n: EmployeeNotificationDTO) => {
    if (!n.read) {
      markOneMutation.mutate(n.id);
    }
  };

  const handleAcknowledge = (e: React.MouseEvent, n: EmployeeNotificationDTO) => {
    e.stopPropagation();
    ackMutation.mutate(n.id);
  };

  return (
    <>
      <button onClick={handleOpen} className="relative p-1">
        <Bell className="h-5 w-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] flex flex-col">
          <SheetHeader className="flex-row items-center justify-between">
            <SheetTitle>Уведомления</SheetTitle>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isPending}
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Прочитать все
              </Button>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">
                Нет уведомлений
              </p>
            ) : (
              <div className="space-y-1">
                {notifications.map((n) => (
                  <button
                    key={n.id}
                    className={cn(
                      'w-full text-left p-3 rounded-lg transition-colors',
                      n.read
                        ? 'text-muted-foreground'
                        : 'bg-primary/5 font-medium'
                    )}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <p className="text-sm">
                      {formatNotificationMessage(n.type, n.payload)}
                    </p>
                    {!n.actorName && (
                      <p className="text-xs text-muted-foreground">Система</p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-muted-foreground">
                        {timeAgo(n.createdAt)}
                      </p>
                      {n.requiresAcknowledgment && (
                        n.acknowledgedAt ? (
                          <span className="flex items-center text-xs text-green-600">
                            <CheckCircle2 className="h-3.5 w-3.5 mr-0.5" />
                            Погашено
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs px-2"
                            onClick={(e) => handleAcknowledge(e, n)}
                            disabled={ackMutation.isPending}
                          >
                            Погасить
                          </Button>
                        )
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
